import type { DocumentGenerationKindDTO } from '@shared/schemas/document-generation';

const KIND_LABELS: Record<DocumentGenerationKindDTO, string> = {
  docx: 'DOCX',
  pdf: 'PDF',
  pptx: 'PPTX',
  xlsx: 'XLSX',
};

const FILE_EXTENSION_PATTERN = /\.([a-z0-9]{2,5})$/i;

/** Libellé de format en majuscules (« DOCX »), depuis le format demandé ou, à défaut, l'extension du fichier. */
export function formatDocumentFormatLabel(
  kind: DocumentGenerationKindDTO | null | undefined,
  filename?: string | null,
): string | null {
  if (kind) {
    return KIND_LABELS[kind];
  }
  const extension = filename ? FILE_EXTENSION_PATTERN.exec(filename)?.[1] : undefined;
  return extension ? extension.toUpperCase() : null;
}

const sizeNumberFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** Taille lisible en unités françaises (base 1024) : « 840 Ko », « 2,4 Mo ». `null` si inconnue. */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return null;
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} o`;
  }
  const kilobytes = bytes / 1024;
  if (kilobytes < 1000) {
    return `${Math.round(kilobytes)} Ko`;
  }
  const megabytes = kilobytes / 1024;
  if (megabytes < 1000) {
    return `${sizeNumberFormat.format(megabytes)} Mo`;
  }
  return `${sizeNumberFormat.format(megabytes / 1024)} Go`;
}

const dayFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
const dayWithYearFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * « Mis à jour aujourd’hui à 14:28 », « … hier à 09:05 », « … le 3 oct. à 14:28 » (l'année
 * n'apparaît que pour une autre année). `null` si la date est absente ou invalide.
 */
export function formatUpdatedAt(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (dayDiff === 0) {
    return `Mis à jour aujourd’hui à ${time}`;
  }
  if (dayDiff === 1) {
    return `Mis à jour hier à ${time}`;
  }
  const day = (date.getFullYear() === now.getFullYear() ? dayFormat : dayWithYearFormat).format(date);
  return `Mis à jour le ${day} à ${time}`;
}
